import React, { useEffect, useMemo, useState } from "react";
import { FaIdCard, FaCheckCircle, FaChevronDown, FaChevronUp } from "react-icons/fa";
import api from "../../utils/api/axiosInstance";
import CollapsibleSection from "../common/CollapsibleSection";
import ShoppingProfileSuggestionBanner from "./ShoppingProfileSuggestionBanner";
import styles from "../../styles/pages/profile/ProfilePage.module.css";
import {
  SHOPPING_FOR_OPTIONS,
  LIFE_ROLE_OPTIONS,
  TECH_RELATIONSHIP_OPTIONS,
  ECOSYSTEM_OPTIONS,
  DEAL_BREAKER_SUGGESTIONS,
} from "../../constants/shoppingIdentity";

const categoryInputId = (name) =>
  `cat-budget-${encodeURIComponent(String(name)).replace(/%/g, "_")}`;

const hasCategoryActivity = (slice) => {
  if (!slice || typeof slice !== "object") return false;
  const dataPoints = slice?.confidence?.dataPoints || 0;
  const band = slice?.budgetBand;
  const hasBand =
    (band?.max != null && Number(band.max) > 0) ||
    (band?.min != null && Number(band.min) > 0);
  return dataPoints >= 2 || hasBand;
};

const sortCategoryNames = (names, { activeCategory, budgetByCategory }) =>
  [...names].sort((a, b) => {
    if (a === activeCategory) return -1;
    if (b === activeCategory) return 1;
    const aSet = budgetByCategory?.[a]?.max > 0;
    const bSet = budgetByCategory?.[b]?.max > 0;
    if (aSet && !bSet) return -1;
    if (!aSet && bSet) return 1;
    return a.localeCompare(b);
  });

const buildVisibleCategoryNames = ({
  modelCats,
  budgetByCategory,
  activeCategory,
  showAll,
}) => {
  const declared = Object.keys(budgetByCategory || {});
  const fromModel = Object.entries(modelCats || {})
    .filter(([key, slice]) => {
      if (key === activeCategory) return true;
      return hasCategoryActivity(slice);
    })
    .map(([key]) => key);

  const relevant = [...new Set([...declared, ...fromModel])];
  const all = [...new Set([...declared, ...Object.keys(modelCats || {})])];

  return {
    visible: sortCategoryNames(showAll ? all : relevant, {
      activeCategory,
      budgetByCategory,
    }),
    totalCatalogCount: all.length,
    relevantCount: relevant.length,
  };
};

const clampBudgetByCategory = (budgetByCategory, ceiling) => {
  if (!ceiling || !budgetByCategory) return budgetByCategory || {};
  const next = { ...budgetByCategory };
  Object.keys(next).forEach((key) => {
    const max = next[key]?.max;
    if (max != null && Number(max) > ceiling) {
      next[key] = { ...next[key], max: ceiling };
    }
  });
  return next;
};

const emptyForm = {
  shoppingFor: "",
  budgetMax: "",
  budgetByCategory: {},
  lifeRole: "",
  techRelationship: "",
  ecosystem: "agnostic",
  dealBreakers: [],
};

const ShoppingIdentitySection = ({ showToast, onSaved }) => {
  const [form, setForm] = useState(emptyForm);
  const [completeness, setCompleteness] = useState(null);
  const [modelCats, setModelCats] = useState({});
  const [activeCategory, setActiveCategory] = useState(null);
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [categoryBudgetsExpanded, setCategoryBudgetsExpanded] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [identityRes, modelRes] = await Promise.all([
          api.get("/users/me/shopping-identity"),
          api.get("/users/me/shopper-model").catch(() => ({ data: null })),
        ]);
        if (cancelled) return;
        const id = identityRes.data?.identity || {};
        const cats = modelRes.data?.shopperModel?.categories || {};
        const active = modelRes.data?.shopperModel?.activeCategory || null;
        const hasDeclaredBudgets = Object.values(id.budgetByCategory || {}).some(
          (entry) => entry?.max > 0,
        );
        setModelCats(cats);
        setActiveCategory(active);
        setShowAllCategories(false);
        setCategoryBudgetsExpanded(hasDeclaredBudgets);
        setForm({
          shoppingFor: id.shoppingFor || "",
          budgetMax: id.budgetMax ?? "",
          budgetByCategory: id.budgetByCategory || {},
          lifeRole: id.lifeRole || "",
          techRelationship: id.techRelationship || "",
          ecosystem: id.ecosystem || "agnostic",
          dealBreakers: id.dealBreakers || [],
        });
        setCompleteness(identityRes.data?.completeness || null);
      } catch {
        if (!cancelled) setCompleteness(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const globalCeiling = useMemo(() => {
    const n = parseInt(form.budgetMax, 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [form.budgetMax]);

  const { visible: categoryNames, totalCatalogCount, relevantCount } = useMemo(
    () =>
      buildVisibleCategoryNames({
        modelCats,
        budgetByCategory: form.budgetByCategory,
        activeCategory,
        showAll: showAllCategories,
      }),
    [modelCats, form.budgetByCategory, activeCategory, showAllCategories],
  );

  useEffect(() => {
    if (!globalCeiling) return;
    setForm((prev) => {
      const clamped = clampBudgetByCategory(prev.budgetByCategory, globalCeiling);
      const unchanged =
        JSON.stringify(clamped) === JSON.stringify(prev.budgetByCategory || {});
      if (unchanged) return prev;
      return { ...prev, budgetByCategory: clamped };
    });
  }, [globalCeiling]);

  const categoryBudgetSummary = useMemo(() => {
    const setCount = Object.values(form.budgetByCategory || {}).filter(
      (entry) => entry?.max > 0,
    ).length;
    return { setCount, shownCount: categoryNames.length };
  }, [form.budgetByCategory, categoryNames.length]);

  const filteredCategoryNames = useMemo(() => {
    const query = categoryFilter.trim().toLowerCase();
    if (!query) return categoryNames;
    return categoryNames.filter((name) => name.toLowerCase().includes(query));
  }, [categoryNames, categoryFilter]);

  const hiddenCategoryCount = Math.max(
    0,
    totalCatalogCount - relevantCount,
  );

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const onCategoryBudgetChange = (category, value) => {
    setForm((p) => {
      const next = { ...(p.budgetByCategory || {}) };
      if (value === "" || value == null) {
        delete next[category];
      } else {
        const max = parseInt(value, 10);
        if (Number.isFinite(max) && max > 0) {
          const capped =
            globalCeiling != null ? Math.min(max, globalCeiling) : max;
          next[category] = { max: capped };
        }
      }
      return { ...p, budgetByCategory: next };
    });
  };

  const toggleDealBreaker = (label) => {
    setForm((p) => {
      const has = p.dealBreakers.includes(label);
      return {
        ...p,
        dealBreakers: has
          ? p.dealBreakers.filter((d) => d !== label)
          : [...p.dealBreakers, label].slice(0, 8),
      };
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const res = await api.patch("/users/me/shopping-identity", {
        shoppingFor: form.shoppingFor,
        budgetMax:
          form.budgetMax === "" ? null : parseInt(form.budgetMax, 10) || null,
        budgetByCategory: form.budgetByCategory,
        lifeRole: form.lifeRole,
        techRelationship: form.techRelationship,
        ecosystem: form.ecosystem,
        dealBreakers: form.dealBreakers,
      });
      setCompleteness(res.data?.completeness || null);
      onSaved?.(res.data);
      showToast?.({
        message: res.data?.message || "Shopping profile saved",
        type: "success",
      });
    } catch (err) {
      showToast?.({
        message:
          err.response?.data?.message || "Failed to save shopping profile",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const pct = completeness?.percent ?? 0;
  const isReady = completeness?.isReady;

  return (
    <CollapsibleSection
      title="Shopping profile"
      subtitle="Who you are for matching - not just what you clicked"
      titleIcon={<FaIdCard className={styles.sectionIcon} />}
      defaultExpanded
      fieldsLayout
    >
      {loading ? (
        <p className={styles.helpText}>Loading your shopping profile…</p>
      ) : (
        <>
          <ShoppingProfileSuggestionBanner
            onAccepted={(data) => {
              const id = data?.identity || {};
              const nextBudgets = id.budgetByCategory || {};
              setForm({
                shoppingFor: id.shoppingFor || "",
                budgetMax: id.budgetMax ?? "",
                budgetByCategory: nextBudgets,
                lifeRole: id.lifeRole || "",
                techRelationship: id.techRelationship || "",
                ecosystem: id.ecosystem || "agnostic",
                dealBreakers: id.dealBreakers || [],
              });
              if (Object.values(nextBudgets).some((entry) => entry?.max > 0)) {
                setCategoryBudgetsExpanded(true);
              }
              setCompleteness(data?.completeness || null);
              onSaved?.(data);
            }}
          />
          <div className={styles.shoppingIdentityProgress} role="status">
            <span className={styles.shoppingIdentityProgressLabel}>
              <span>Profile {pct}% complete</span>
              {isReady && (
                <span className={styles.shoppingIdentityReady}>
                  <FaCheckCircle aria-hidden className={styles.inlineOk} />
                  <span>Ready for confident scores</span>
                </span>
              )}
            </span>
            <div className={styles.shoppingIdentityProgressBar} aria-hidden>
              <div
                className={styles.shoppingIdentityProgressFill}
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>

          <span>
            <label htmlFor="shoppingFor">
              Who are you usually shopping for?
            </label>
            <select
              id="shoppingFor"
              name="shoppingFor"
              value={form.shoppingFor}
              onChange={onChange}
            >
              <option value="">Select…</option>
              {SHOPPING_FOR_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </span>

          <span>
            <label htmlFor="budgetMax">Overall budget ceiling (USD)</label>
            <input
              id="budgetMax"
              name="budgetMax"
              type="number"
              min="0"
              value={form.budgetMax}
              onChange={onChange}
              placeholder="e.g. 1200 — max across all categories"
            />
          </span>

          {totalCatalogCount > 0 && (
            <div className={styles.categoryBudgetSection}>
              <button
                type="button"
                className={`${styles.categoryBudgetToggle} ${
                  categoryBudgetsExpanded ? styles.categoryBudgetToggleOpen : ""
                }`}
                onClick={() => setCategoryBudgetsExpanded((open) => !open)}
                aria-expanded={categoryBudgetsExpanded}
                aria-controls="category-budget-panel"
              >
                <span className={styles.categoryBudgetToggleText}>
                  <span className={styles.categoryBudgetToggleTitle}>
                    Category budgets (optional)
                  </span>
                  <span className={styles.categoryBudgetToggleMeta}>
                    {categoryBudgetSummary.setCount > 0
                      ? `${categoryBudgetSummary.setCount} set`
                      : "None set"}
                    {" · "}
                    {showAllCategories
                      ? `${totalCatalogCount} categories`
                      : `${categoryBudgetSummary.shownCount} active`}
                    {activeCategory ? ` · focus: ${activeCategory}` : ""}
                  </span>
                </span>
                <span className={styles.categoryBudgetToggleIcon} aria-hidden>
                  {categoryBudgetsExpanded ? <FaChevronUp /> : <FaChevronDown />}
                </span>
              </button>

              {categoryBudgetsExpanded && (
                <div
                  id="category-budget-panel"
                  className={styles.categoryBudgetPanel}
                >
                  <p className={styles.helpText}>
                    Per-category limits override the overall ceiling when you
                    shop that category.
                    {globalCeiling
                      ? ` Each is capped at $${globalCeiling.toLocaleString()}.`
                      : " Set an overall ceiling first to cap category limits."}
                  </p>

                  {categoryNames.length > 8 && (
                    <input
                      type="search"
                      className={styles.categoryBudgetSearch}
                      value={categoryFilter}
                      onChange={(e) => setCategoryFilter(e.target.value)}
                      placeholder="Filter categories…"
                      aria-label="Filter categories"
                    />
                  )}

                  <div className={styles.categoryBudgetList}>
                    {filteredCategoryNames.length === 0 ? (
                      <p className={styles.helpText}>No categories match.</p>
                    ) : (
                      filteredCategoryNames.map((category) => {
                        const inputId = categoryInputId(category);
                        return (
                          <div key={category} className={styles.categoryBudgetRow}>
                            <label htmlFor={inputId}>
                              {category}
                              {category === activeCategory ? (
                                <span className={styles.categoryBudgetActiveTag}>
                                  Active
                                </span>
                              ) : null}
                            </label>
                            <input
                              id={inputId}
                              type="number"
                              min="0"
                              max={globalCeiling || undefined}
                              value={
                                form.budgetByCategory?.[category]?.max ?? ""
                              }
                              onChange={(e) =>
                                onCategoryBudgetChange(category, e.target.value)
                              }
                              placeholder={
                                globalCeiling
                                  ? `Up to ${globalCeiling}`
                                  : "e.g. 600"
                              }
                            />
                          </div>
                        );
                      })
                    )}
                  </div>

                  {!showAllCategories && hiddenCategoryCount > 0 && (
                    <button
                      type="button"
                      className={styles.categoryBudgetShowAll}
                      onClick={() => setShowAllCategories(true)}
                    >
                      Show all {totalCatalogCount} categories
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          <span>
            <label htmlFor="lifeRole">Life context</label>
            <select
              id="lifeRole"
              name="lifeRole"
              value={form.lifeRole}
              onChange={onChange}
            >
              <option value="">Select…</option>
              {LIFE_ROLE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </span>

          <span>
            <label htmlFor="techRelationship">How you use tech</label>
            <select
              id="techRelationship"
              name="techRelationship"
              value={form.techRelationship}
              onChange={onChange}
            >
              <option value="">Select…</option>
              {TECH_RELATIONSHIP_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </span>

          <span>
            <label htmlFor="ecosystem">Ecosystem preference</label>
            <select
              id="ecosystem"
              name="ecosystem"
              value={form.ecosystem}
              onChange={onChange}
            >
              {ECOSYSTEM_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </span>

          <span>
            <label>Deal-breakers (optional)</label>
            <div className={styles.dealBreakerChips}>
              {DEAL_BREAKER_SUGGESTIONS.map((label) => (
                <button
                  key={label}
                  type="button"
                  className={`${styles.dealBreakerChip} ${
                    form.dealBreakers.includes(label)
                      ? styles.dealBreakerChipOn
                      : ""
                  }`}
                  onClick={() => toggleDealBreaker(label)}
                >
                  {label}
                </button>
              ))}
            </div>
          </span>

          <div className={styles.preferencesButtonRow}>
            <button
              type="button"
              className="buttonSecondary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving…" : "Save shopping profile"}
            </button>
          </div>
        </>
      )}
    </CollapsibleSection>
  );
};

export default ShoppingIdentitySection;
